import React, { useState, useRef, useEffect } from 'react';
import { Sparkles, X, Send, Loader2, ChevronDown, Folder, FileText, Trash2, Edit3, FileDown } from 'lucide-react';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  actions?: ActionResult[];
}

interface ActionResult {
  action: string;
  success: boolean;
  message?: string;
  summary?: string;
  preview?: string;
  error?: string;
}

interface FloatingAssistantProps {
  clientId: string;
  onFileChange?: () => void; // Callback pour rafraîchir la liste des fichiers
  attachedDocument?: string | null; // Document attaché au contexte
  onDetachDocument?: () => void; // Callback pour détacher le document
}

export default function FloatingAssistant({ clientId, onFileChange, attachedDocument, onDetachDocument }: FloatingAssistantProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll vers le bas
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input quand on ouvre
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await fetch(`/api/portal/clients/${clientId}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage,
          history: messages.slice(-10), // Garder les 10 derniers messages pour le contexte
          attachedDocument: attachedDocument || undefined // Document attaché au contexte
        })
      });

      const data = await response.json();

      const assistantMessage: Message = {
        role: 'assistant',
        content: data.message || 'Réponse reçue.',
        actions: data.actionResults
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Si des actions ont été effectuées, rafraîchir la liste des fichiers
      if (data.actionResults?.some((a: ActionResult) => a.success)) {
        onFileChange?.();
      }

    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Désolé, une erreur est survenue. Veuillez réessayer.'
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'create_folder': return <Folder className="w-4 h-4" />;
      case 'rename': return <Edit3 className="w-4 h-4" />;
      case 'delete': return <Trash2 className="w-4 h-4" />;
      case 'edit': return <Edit3 className="w-4 h-4" />;
      case 'append': return <FileText className="w-4 h-4" />;
      case 'summarize': return <FileDown className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const quickActions = [
    { label: 'Lister les fichiers', prompt: 'Liste tous les fichiers du projet' },
    { label: 'Créer un dossier', prompt: 'Crée un dossier nommé "nouveau_dossier"' },
    { label: 'Résumer MASTERPLAN', prompt: 'Résume le fichier MASTERPLAN.md' },
  ];

  return (
    <>
      {/* Bouton flottant */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 ${
          isOpen
            ? 'bg-gray-800 text-white rotate-180'
            : 'bg-gradient-to-br from-blue-600 to-purple-600 text-white hover:scale-110'
        }`}
        title="Assistant IA"
      >
        {isOpen ? <X className="w-6 h-6" /> : <Sparkles className="w-6 h-6" />}
      </button>

      {/* Fenêtre de chat */}
      {isOpen && (
        <div
          className={`fixed bottom-24 right-6 z-50 bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden transition-all duration-300 ${
            isExpanded ? 'w-[600px] h-[700px]' : 'w-[380px] h-[500px]'
          }`}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              <span className="font-semibold">Assistant Portail</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="p-1 hover:bg-white/20 rounded transition-colors"
                title={isExpanded ? 'Réduire' : 'Agrandir'}
              >
                <ChevronDown className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.length === 0 && (
              <div className="text-center text-gray-500 py-8">
                <Sparkles className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p className="mb-4">Comment puis-je vous aider ?</p>
                <div className="space-y-2">
                  {quickActions.map((action, i) => (
                    <button
                      key={i}
                      onClick={() => {
                        setInput(action.prompt);
                        inputRef.current?.focus();
                      }}
                      className="block w-full text-left px-3 py-2 bg-white rounded-lg border border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-colors text-sm"
                    >
                      {action.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, i) => (
              <div
                key={i}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-2 shadow-sm ${
                    msg.role === 'user'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white border border-gray-200'
                  }`}
                >
                  <p className={`whitespace-pre-wrap ${msg.role === 'user' ? 'text-white' : 'text-gray-800'}`}>
                    {msg.content}
                  </p>

                  {/* Actions effectuées */}
                  {msg.actions && msg.actions.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
                      {msg.actions.map((action, j) => (
                        <div
                          key={j}
                          className={`flex items-center gap-2 text-sm font-medium ${
                            action.success ? 'text-green-700' : 'text-red-700'
                          }`}
                        >
                          {getActionIcon(action.action)}
                          <span>{action.message || action.action}</span>
                          {action.success ? '✓' : '✗'}
                        </div>
                      ))}

                      {/* Afficher le résumé si présent */}
                      {msg.actions.find(a => a.summary) && (
                        <div className="mt-2 p-2 bg-gray-100 rounded-lg text-sm text-gray-800">
                          <strong className="text-gray-900">Résumé:</strong>
                          <p className="mt-1 text-gray-700">{msg.actions.find(a => a.summary)?.summary}</p>
                        </div>
                      )}

                      {/* Afficher la preview si présent */}
                      {msg.actions.find(a => a.preview) && (
                        <div className="mt-2 p-2 bg-gray-100 rounded-lg text-sm">
                          <strong className="text-gray-900">Aperçu:</strong>
                          <p className="mt-1 text-gray-700 text-xs font-mono">
                            {msg.actions.find(a => a.preview)?.preview}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 rounded-2xl px-4 py-3">
                  <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 bg-white border-t border-gray-200">
            {/* Document attaché */}
            {attachedDocument && (
              <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border border-blue-200">
                <span className="text-blue-600 font-bold">@</span>
                <span className="flex-1 text-sm text-blue-800 font-medium truncate">
                  {attachedDocument.split('/').pop()?.replace(/\.[^/.]+$/, '')}
                </span>
                <button
                  onClick={onDetachDocument}
                  className="p-1 text-blue-400 hover:text-red-500 hover:bg-white rounded transition-colors"
                  title="Détacher le document"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Demandez-moi quelque chose..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                disabled={isLoading}
              />
              <button
                onClick={sendMessage}
                disabled={isLoading || !input.trim()}
                className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-2 text-center">
              Commandes: créer dossier, renommer, supprimer, résumer, modifier...
            </p>
          </div>
        </div>
      )}
    </>
  );
}
